import { match } from 'ts-pattern';

import { setModalLoggerContextProperty } from '@widget/core/utils/widgetModalLoggerContext';
import { accumulateFormData } from './calculateNextState';

import type { WidgetState } from '../types';
import type { Action } from './types';

export type BackAction = Action<'BACK'>;
export type NextAction = Action<'NEXT'>;
export type ResetAction = Action<'RESET'>;
export type GoToStepAction = Action<'GO_TO_STEP', { id: string }>;
export type SkipAction = Action<'SKIP_STEP', { id: string }>;
export type CommitAction = Action<'COMMIT_STEP', { id: string; stepData: Record<string, unknown> }>;
export type ResetHistoryAction = Action<'RESET_HISTORY', { id: string; nextStepId: string }>;
export type ResetFormAction = Action<'RESET_FORM'>;

export type ReducerActions =
  | BackAction
  | NextAction
  | ResetAction
  | GoToStepAction
  | CommitAction
  | SkipAction
  | ResetHistoryAction
  | ResetFormAction;

export function reducer(state: WidgetState, action: ReducerActions): WidgetState {
  return match<ReducerActions>(action)
    .with({ type: 'RESET_HISTORY' }, resetHistory(state))
    .with({ type: 'COMMIT_STEP' }, commitStep(state))
    .with({ type: 'SKIP_STEP' }, skipStep(state))
    .with({ type: 'NEXT' }, nextStep(state))
    .with({ type: 'BACK' }, prevStep(state))
    .with({ type: 'GO_TO_STEP' }, goToStep(state))
    .with({ type: 'RESET' }, resetState(state))
    .with({ type: 'RESET_FORM' }, resetForm(state))
    .otherwise(() => state);
}

export function commitStep(state: WidgetState) {
  return (action: CommitAction): WidgetState => {
    const { id, stepData } = action.payload;

    setModalLoggerContextProperty('currentStepId', id);

    return {
      ...state,
      history: state.history.map((step) => ({
        ...step,
        ...(id === step.id && {
          formData: stepData,
          skipped: false,
        }),
      })),
    };
  };
}

export function skipStep(state: WidgetState) {
  return (action: SkipAction): WidgetState => {
    const { id } = action.payload;

    return {
      ...state,
      history: state.history.map((step) => ({
        ...step,
        skipped: step.skipped || id === step.id,
      })),
    };
  };
}

/**
 * Recalculate a history based on a current and a next steps
 * So that if:
 * current step is the last step in the history, then we add the next step to the history
 * next step is followed by current step as it is in the history, then we iterate of the rest of the history and recalculate whether they should be preserved or dropped based on the `renderWhen` prop
 * nextStep is not followed by current step, then we add the next to the history
 * All the dropped steps are saved to stepDataMap.
 */
export function resetHistory(state: WidgetState) {
  return ({ payload: { id: currentStepId, nextStepId } }: ResetHistoryAction): WidgetState => {
    const currentStepIndex = state.history.findIndex(({ id }) => id === currentStepId);
    const history = state.history.slice(0, currentStepIndex + 1);
    const historyTail = state.history.slice(currentStepIndex + 1);
    let shouldResetSteps = nextStepId !== historyTail[0]?.id;
    let cumulativeFormData = accumulateFormData(history);

    if (!historyTail.length || shouldResetSteps) {
      history.push({
        id: nextStepId,
        skipped: false,
        formData: state.stepDataMap[nextStepId] || {},
      });
    }

    return historyTail.reduce(
      (state, stepData) => {
        const { id } = stepData;

        // reset step if one of the steps before was reset or if current doesn't meet when condition
        if (
          shouldResetSteps ||
          state.stepPropsMap[id]?.renderWhen?.(cumulativeFormData) === false
        ) {
          shouldResetSteps = true;
          return {
            ...state,
            stepDataMap: {
              ...state.stepDataMap,
              [id]: stepData.formData,
            },
          };
        }
        // otherwise try to preserve history
        cumulativeFormData = {
          ...cumulativeFormData,
          ...(!stepData.skipped && stepData.formData),
        };
        return {
          ...state,
          history: [...state.history, stepData],
        };
      },
      {
        ...state,
        history,
        activeId: nextStepId,
      },
    );
  };
}

export function nextStep(state: WidgetState) {
  return (): WidgetState => {
    const index = state.history.findIndex(({ id }) => id === state.activeId);
    const nextIndex = Math.min(state.history.length - 1, index + 1);

    return {
      ...state,
      activeId: state.history[nextIndex]?.id as string,
    };
  };
}

export function resetState(state: WidgetState) {
  return (): WidgetState => {
    return {
      stepDataMap: state.stepDataMap,
      stepPropsMap: state.stepPropsMap,
      activeId: state.history[0]?.id || '',
      history: [{ id: state.history[0]?.id || '', formData: {}, skipped: false }],
    };
  };
}

export function resetForm(state: WidgetState) {
  return (): WidgetState => {
    return {
      ...state,
      stepDataMap: {},
      history: [{ id: state.activeId || '', formData: {}, skipped: false }],
    };
  };
}

export function prevStep(state: WidgetState) {
  return (): WidgetState => {
    const index = state.history.findIndex(({ id }) => id === state.activeId);
    const prevIndex = Math.max(0, index - 1);

    return {
      ...state,
      activeId: state.history[prevIndex]?.id as string,
    };
  };
}

export function goToStep(state: WidgetState) {
  return (action: GoToStepAction): WidgetState => {
    const { id } = action.payload;

    return {
      ...state,
      activeId: id,
    };
  };
}
